import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Shield, ExternalLink, Clock } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api.js';
import { Card, Field, SaveButton, isDirty, LoadingPanel } from './_shared.jsx';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ComplianceTab() {
  const [windows, setWindows] = useState(null);
  const [windowsDraft, setWindowsDraft] = useState(null);
  const [tz, setTz] = useState('America/Denver');
  const [tzDraft, setTzDraft] = useState('America/Denver');
  const [busy, setBusy] = useState(false);
  const [dnc, setDnc] = useState(null);

  useEffect(() => {
    apiGet('/settings/quiet-hours').then((r) => {
      const existing = Array.isArray(r?.sendWindowsByDay) ? r.sendWindowsByDay : [];
      const merged = DAYS.map((_, day) => {
        const found = existing.find((w) => w.day === day);
        return found || { day, start: '08:00', end: '21:00', closed: false };
      });
      setWindows(merged);
      setWindowsDraft(merged);
      setTz(r?.sendWindowsTimezone || 'America/Denver');
      setTzDraft(r?.sendWindowsTimezone || 'America/Denver');
    }).catch((e) => toast.error(e.message || 'Failed to load compliance windows'));
    apiGet('/settings/dnc-summary').then((r) => {
      if (r?.ok) setDnc({ total: r.total, recent: r.recent });
    }).catch(() => setDnc({ total: 0, recent: [] }));
  }, []);

  function update(day, patch) {
    setWindowsDraft((w) => w.map((row) => row.day === day ? { ...row, ...patch } : row));
  }

  async function save() {
    const prevW = windows; const prevTz = tz;
    setBusy(true);
    setWindows(windowsDraft); setTz(tzDraft);
    try {
      await apiPut('/settings/quiet-hours', { sendWindowsByDay: windowsDraft, sendWindowsTimezone: tzDraft });
      toast.success('Compliance windows saved');
    } catch (e) {
      setWindows(prevW); setTz(prevTz);
      setWindowsDraft(prevW); setTzDraft(prevTz);
      toast.error(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  if (!windowsDraft) return <LoadingPanel label="Loading compliance windows…" />;
  const dirty = isDirty(windowsDraft, windows) || tzDraft !== tz;

  return (
    <div className="space-y-4">
      <Card
        title="Quiet Hours / Send Windows"
        description="Per-day TCPA send windows. Closed days block outbound SMS, drips, and reminders."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <Field label="Timezone">
          <select className="input max-w-xs" value={tzDraft} onChange={(e) => setTzDraft(e.target.value)}>
            {['America/Denver', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'America/Phoenix'].map((z) => (
              <option key={z}>{z}</option>
            ))}
          </select>
        </Field>

        <div className="space-y-2">
          <div className="grid grid-cols-[80px_1fr_1fr_90px] gap-3 text-xs uppercase tracking-wider text-white/40 px-2">
            <div>Day</div><div>Start</div><div>End</div><div>Closed</div>
          </div>
          {windowsDraft.map((row) => (
            <div key={row.day} className="grid grid-cols-[80px_1fr_1fr_90px] gap-3 items-center px-2 py-2 rounded-md bg-civix-panelLight border border-civix-border/60">
              <div className="text-sm font-semibold text-white/80">{DAYS[row.day]}</div>
              <input type="time" value={row.start} disabled={row.closed}
                onChange={(e) => update(row.day, { start: e.target.value })}
                className="input py-1.5 disabled:opacity-30"/>
              <input type="time" value={row.end} disabled={row.closed}
                onChange={(e) => update(row.day, { end: e.target.value })}
                className="input py-1.5 disabled:opacity-30"/>
              <label className="flex items-center gap-2 text-sm text-white/70">
                <input type="checkbox" checked={!!row.closed}
                  onChange={(e) => update(row.day, { closed: e.target.checked })}/>
                Closed
              </label>
            </div>
          ))}
        </div>
      </Card>

      <Card
        title="Compliance Log"
        description="Pre-send compliance evaluations for outbound SMS (TCPA, state insurance, carrier rules)."
        actions={
          <Link to="/compliance/log" className="btn-ghost text-sm inline-flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> View Log
          </Link>
        }
      >
        <div className="text-xs text-white/60">
          Every outbound SMS runs through a mechanical fast-path and an optional LLM review before
          send. Blocking verdicts prevent the send and write an AuditLog entry.
        </div>
      </Card>

      <Card
        title="Internal DNC List"
        description="Numbers your tenant has suppressed from outbound contact. STOP / UNSUBSCRIBE keywords are added here automatically. CIVIX does not scrub leads against National DNC, state DNC, or the Reassigned Numbers Database — that is the agent's responsibility."
      >
        <div className="grid md:grid-cols-2 gap-3">
          <div className="p-4 rounded-lg bg-civix-panelLight border border-civix-border/60">
            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider">
              <Shield className="w-3.5 h-3.5" /> Total Suppressed
            </div>
            <div className="font-display text-3xl font-bold mt-1">{dnc?.total ?? '—'}</div>
          </div>
          <div className="p-4 rounded-lg bg-civix-panelLight border border-civix-border/60">
            <div className="flex items-center gap-2 text-white/60 text-xs uppercase tracking-wider mb-2">
              <Clock className="w-3.5 h-3.5" /> Recent
            </div>
            {!dnc?.recent?.length ? (
              <div className="text-xs text-white/40">No entries yet.</div>
            ) : (
              <div className="space-y-1 text-xs">
                {dnc.recent.slice(0, 5).map((e) => (
                  <div key={e._id} className="flex items-center justify-between text-white/70">
                    <span className="font-mono">{e.phone}</span>
                    <span className="text-white/40">{e.reason || '—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
